import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Button, Box, TextField,
  FormControl, FormLabel, Alert, Slider, CircularProgress
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SurveyDefinition, SurveyQuestion } from '../types';
import { surveyApi } from '../utils/api';

interface EndSemesterResponses {
  strengths_accuracy?: number;
  weaknesses_accuracy?: number;
  feedback_helped?: number;
  glad_took_assessment?: number;
  likelihood_recommend?: number;
  advice?: string;
}

export default function EndSemesterSurveyPage() {
  const navigate = useNavigate();
  const { data: definition, isLoading: loading } = useQuery<SurveyDefinition>({
    queryKey: ['survey', 'definition', 'endterm'],
    queryFn: () => surveyApi.getDefinition('endterm'),
  });
  const [responses, setResponses] = useState<EndSemesterResponses>({});
  const [error, setError] = useState('');

  const handleChange = (questionId: string, value: string | number) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const submitMutation = useMutation({
    mutationFn: (r: EndSemesterResponses) => surveyApi.submitEndSurvey(r),
    onSuccess: () => navigate('/assessment'),
    onError: () => setError('Failed to submit survey. Please try again.'),
  });

  const handleSubmit = () => {
    setError('');
    submitMutation.mutate(responses);
  };

  const renderQuestion = (question: SurveyQuestion, index: number) => {
    const questionNumber = index + 1;
    
    switch (question.type) {
      case 'scale':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`}>
              {questionNumber}. {question.question}
            </FormLabel>
            <Box sx={{ px: 2, pt: 2 }}>
              <Slider
                value={responses[question.id as keyof EndSemesterResponses] as number || question.min}
                onChange={(_, value) => handleChange(question.id, value as number)}
                min={question.min}
                max={question.max}
                step={1}
                marks={[
                  { value: question.min!, label: question.min_label },
                  { value: question.max!, label: question.max_label },
                ]}
                valueLabelDisplay="auto"
                aria-labelledby={`question-${question.id}-label`}
              />
            </Box>
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {questionNumber}. {question.question}
            </FormLabel>
            <TextField
              value={responses[question.id as keyof EndSemesterResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              multiline
              rows={3}
              variant="outlined"
              inputProps={{
                'aria-labelledby': `question-${question.id}-label`,
              }}
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 800, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography>Loading survey...</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!definition) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 800, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error">{error || 'Failed to load survey'}</Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Card>
          <CardContent sx={{ p: 8 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              {definition.title}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Estimated time: {definition.estimated_time}
            </Typography>

            <Typography variant="body2" paragraph sx={{ fontSize: '1rem', mb: 5 }}>
              {definition.instructions}
            </Typography>

            <Box component="form" noValidate>
              {definition.questions.map((question, index) => renderQuestion(question, index))}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} role="alert">
                {error}
              </Alert>
            )}

            <Box textAlign="center" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/assessment')}
                disabled={submitMutation.isPending}
                sx={{ px: 6, mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={submitMutation.isPending}
                sx={{ px: 6 }}
              >
                {submitMutation.isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>

            <Box textAlign="center" sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Thank you for sharing your feedback and for being part of this study!
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
